import * as XLSX from "xlsx-js-style";
import { nomeDeAba } from "../../components/DataTable";
import type { LinhaRelatorioN4 } from "./metasRelatorioN4";

/** Quantos itens do topo (maior venda do mês anterior) ficam em destaque, como na planilha do cliente. */
export const ITENS_EM_DESTAQUE = 6;

const LARANJA = "F79646";
const PESSEGO = "FCD5B4";
const AMARELO = "FFFF00";

const FORMATO_MOEDA = '"R$ "#,##0';
const FORMATO_PERCENTUAL = "0%";
const FORMATO_PARTICIPACAO = "0.00%";

const COLUNAS = [
  { rotulo: "Seção (N3)", largura: 18 },
  { rotulo: "Subseção (N4)", largura: 30 },
  { rotulo: "% Vda", largura: 9 },
  { rotulo: "VDA realiz", largura: 15 },
  { rotulo: "VDA projet", largura: 15 },
  { rotulo: "CONT projet", largura: 15 },
  { rotulo: "MG proj", largura: 9 },
  { rotulo: "VDA projet/dia", largura: 15 },
];

const borda = { style: "thin", color: { rgb: "000000" } };
const bordas = { top: borda, bottom: borda, left: borda, right: borda };

const estiloCabecalho = (tamanho = 9) => ({
  font: { name: "Arial", sz: tamanho, bold: true },
  fill: { patternType: "solid", fgColor: { rgb: LARANJA } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: bordas,
});

export interface OpcoesExcelN4 {
  /** Ex.: "OUTUBRO 2.026". */
  titulo: string;
  /** Ex.: "set/26" — o mês da venda realizada. */
  rotuloAnterior: string;
  /** Ex.: "out/26" — o mês da meta. */
  rotuloMeta: string;
  nomeArquivo: string;
}

/** Uma aba no estilo da planilha do cliente: cabeçalho laranja com categoria e meses, maiores itens em destaque e TOTAL em amarelo. */
function montarAba(categoria: string, linhas: LinhaRelatorioN4[], opcoes: OpcoesExcelN4) {
  const ultimaColuna = COLUNAS.length - 1;
  const celulas: XLSX.CellObject[][] = [];
  const vazia = (estilo: object): XLSX.CellObject => ({ t: "s", v: "", s: estilo });

  const linha1 = COLUNAS.map(() => vazia(estiloCabecalho()));
  linha1[0] = { t: "s", v: `${opcoes.titulo} — Meta`, s: estiloCabecalho(11) };
  linha1[3] = { t: "s", v: categoria.toUpperCase(), s: estiloCabecalho(14) };
  const linha2 = COLUNAS.map(() => vazia(estiloCabecalho()));
  linha2[3] = { t: "s", v: opcoes.rotuloAnterior, s: estiloCabecalho(11) };
  linha2[4] = { t: "s", v: opcoes.rotuloMeta, s: estiloCabecalho(11) };
  const linha3 = COLUNAS.map((coluna) => ({ t: "s", v: coluna.rotulo, s: estiloCabecalho() }) as XLSX.CellObject);
  celulas.push(linha1, linha2, linha3);

  let itensVistos = 0;
  linhas.forEach((linha) => {
    const total = linha.linhaTipo === "TOTAL";
    const destaque = !total && itensVistos++ < ITENS_EM_DESTAQUE;
    const base = {
      font: { name: "Arial", sz: 9, bold: total || destaque, italic: destaque },
      border: bordas,
      ...(total
        ? { fill: { patternType: "solid", fgColor: { rgb: AMARELO } } }
        : destaque
          ? { fill: { patternType: "solid", fgColor: { rgb: PESSEGO } } }
          : {}),
    };
    const texto = (v: string): XLSX.CellObject => ({ t: "s", v, s: { ...base, alignment: { vertical: "center" } } });
    const numero = (v: number | null, z: string): XLSX.CellObject =>
      v === null ? { t: "s", v: "", s: base } : { t: "n", v, z, s: { ...base, alignment: { horizontal: "right" } } };
    celulas.push([
      texto(linha.secao),
      texto(total ? "TOTAL" : linha.subsecao),
      numero(linha.participacaoMesAnterior, FORMATO_PARTICIPACAO),
      numero(linha.vendaMesAnterior, FORMATO_MOEDA),
      numero(linha.metaVenda, FORMATO_MOEDA),
      numero(linha.metaContribuicao, FORMATO_MOEDA),
      numero(linha.metaMargem, FORMATO_PERCENTUAL),
      numero(linha.metaVendaDia, FORMATO_MOEDA),
    ]);
  });

  const aba: XLSX.WorkSheet = {};
  celulas.forEach((linha, r) => linha.forEach((celula, c) => (aba[XLSX.utils.encode_cell({ r, c })] = celula)));
  aba["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: celulas.length - 1, c: ultimaColuna } });
  aba["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 2 } },
    { s: { r: 0, c: 3 }, e: { r: 0, c: ultimaColuna } },
    { s: { r: 1, c: 4 }, e: { r: 1, c: ultimaColuna } },
  ];
  aba["!cols"] = COLUNAS.map((coluna) => ({ wch: coluna.largura }));
  aba["!rows"] = [{ hpt: 24 }, { hpt: 18 }, { hpt: 20 }];
  return aba;
}

/** Grava o relatório N4 num .xlsx estilizado, uma aba por categoria (`linhasPorCategoria` traz os itens e o TOTAL de cada uma). */
export function exportarRelatorioN4Estilizado(linhasPorCategoria: Map<string, LinhaRelatorioN4[]>, opcoes: OpcoesExcelN4) {
  const workbook = XLSX.utils.book_new();
  const usados = new Set<string>();
  linhasPorCategoria.forEach((linhas, categoria) =>
    XLSX.utils.book_append_sheet(workbook, montarAba(categoria, linhas, opcoes), nomeDeAba(categoria, usados)),
  );
  XLSX.writeFile(workbook, `${opcoes.nomeArquivo}.xlsx`);
}
